import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Building2, Activity, Send, Check, Loader2 } from "lucide-react";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

// Schemas per step
const step1Schema = z.object({
  company_name: z.string().trim().min(1, "Required").max(200),
  website_url: z.string().max(500).optional(),
});

const step3Schema = z.object({
  name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
});

const STEPS = [1, 2, 3] as const;

const SystemsWhispererSection = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Form state
  const [companyName, setCompanyName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [cac, setCac] = useState(500);
  const [churnRate, setChurnRate] = useState(5);
  const [ltv, setLtv] = useState(5000);
  const [bottleneck, setBottleneck] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const stepIcons = [Building2, Activity, Send];
  const stepLabels = [
    t("whisperer.step1.title"),
    t("whisperer.step2.title"),
    t("whisperer.step3.title"),
  ];

  const bottleneckOptions = [
    { value: "manual_sales", label: t("whisperer.bottleneck.manual") },
    { value: "low_quality_leads", label: t("whisperer.bottleneck.leads") },
    { value: "high_churn", label: t("whisperer.bottleneck.churn") },
    { value: "confusing_messaging", label: t("whisperer.bottleneck.messaging") },
  ];

  const validateStep = (step: number): boolean => {
    setErrors({});
    if (step === 1) {
      const result = step1Schema.safeParse({ company_name: companyName, website_url: websiteUrl });
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((e) => { fieldErrors[e.path[0] as string] = e.message; });
        setErrors(fieldErrors);
        return false;
      }
    }
    if (step === 2) {
      if (!bottleneck) {
        setErrors({ bottleneck: "Required" });
        return false;
      }
    }
    if (step === 3) {
      const result = step3Schema.safeParse({ name, email });
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((e) => { fieldErrors[e.path[0] as string] = e.message; });
        setErrors(fieldErrors);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("audit-form", {
        body: {
          company_name: companyName.trim(),
          website_url: websiteUrl.trim(),
          cac,
          churn_rate: churnRate,
          ltv,
          bottleneck,
          name: name.trim(),
          email: email.trim(),
        },
      });
      if (error) throw error;
      setIsSubmitted(true);
      toast({
        title: t("whisperer.toast.title"),
        description: t("whisperer.toast.description"),
      });
    } catch {
      toast({
        title: "Error",
        description: t("whisperer.toast.error"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, hsla(152, 82%, 44%, 0.15), transparent 70%)" }} />
        </div>
        <div className="section-container relative z-10 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8" style={{ background: "#12cb89" }}>
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t("whisperer.success.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              {t("whisperer.success.description")}
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, hsla(217, 97%, 58%, 0.1), transparent 70%)" }} />
      </div>

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
            {t("whisperer.title")} <span style={{ color: "#2a7ffc" }}>{t("whisperer.titleHighlight")}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("whisperer.subtitle")}
          </p>
        </motion.div>

        {/* Stepper indicator */}
        <div className="flex items-center justify-center mb-12 max-w-md mx-auto">
          {STEPS.map((step, i) => {
            const Icon = stepIcons[i];
            const isActive = currentStep === step;
            const isCompleted = currentStep > step;
            return (
              <div key={step} className="flex items-center flex-1 last:flex-initial">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                    style={{
                      borderColor: isCompleted ? "#12cb89" : isActive ? "#2a7ffc" : "hsl(var(--border))",
                      background: isCompleted ? "#12cb89" : isActive ? "#2a7ffc" : "transparent",
                    }}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className="w-5 h-5" style={{ color: isActive ? "#fff" : "hsl(var(--muted-foreground))" }} />
                    )}
                  </div>
                  <span className="text-xs font-medium hidden sm:block" style={{ color: isActive ? "#2a7ffc" : isCompleted ? "#12cb89" : "hsl(var(--muted-foreground))" }}>
                    {stepLabels[i]}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 mx-3 mt-[-1.25rem] sm:mt-[-0.5rem] transition-colors duration-300" style={{ background: isCompleted ? "#12cb89" : "hsl(var(--border))" }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="max-w-xl mx-auto">
          <div className="card-surface p-8 sm:p-10">
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h3 className="font-display text-xl font-semibold text-foreground">{t("whisperer.step1.heading")}</h3>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">{t("whisperer.step1.company")} <span className="text-destructive">*</span></Label>
                    <Input id="company_name" placeholder={t("whisperer.step1.companyPlaceholder")} value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={errors.company_name ? "border-destructive" : ""} />
                    {errors.company_name && <p className="text-sm text-destructive">{errors.company_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website_url">{t("whisperer.step1.url")}</Label>
                    <Input id="website_url" placeholder={t("whisperer.step1.urlPlaceholder")} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-8">
                  <h3 className="font-display text-xl font-semibold text-foreground">{t("whisperer.step2.heading")}</h3>

                  {/* CAC Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>{t("whisperer.step2.cac")}</Label>
                      <span className="text-sm font-semibold" style={{ color: "#2a7ffc" }}>${cac.toLocaleString()}</span>
                    </div>
                    <Slider value={[cac]} onValueChange={([v]) => setCac(v)} min={0} max={5000} step={50} className="[&_[role=slider]]:border-[#2a7ffc] [&_[role=slider]]:bg-background [&_span:first-child>span]:bg-[#2a7ffc]" />
                    <div className="flex justify-between text-xs text-muted-foreground"><span>$0</span><span>$5,000</span></div>
                  </div>

                  {/* Churn Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>{t("whisperer.step2.churn")}</Label>
                      <span className="text-sm font-semibold" style={{ color: "#2a7ffc" }}>{churnRate}%</span>
                    </div>
                    <Slider value={[churnRate]} onValueChange={([v]) => setChurnRate(v)} min={0} max={20} step={0.5} className="[&_[role=slider]]:border-[#2a7ffc] [&_[role=slider]]:bg-background [&_span:first-child>span]:bg-[#2a7ffc]" />
                    <div className="flex justify-between text-xs text-muted-foreground"><span>0%</span><span>20%</span></div>
                  </div>

                  {/* LTV Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>{t("whisperer.step2.ltv")}</Label>
                      <span className="text-sm font-semibold" style={{ color: "#2a7ffc" }}>${ltv.toLocaleString()}</span>
                    </div>
                    <Slider value={[ltv]} onValueChange={([v]) => setLtv(v)} min={0} max={20000} step={100} className="[&_[role=slider]]:border-[#2a7ffc] [&_[role=slider]]:bg-background [&_span:first-child>span]:bg-[#2a7ffc]" />
                    <div className="flex justify-between text-xs text-muted-foreground"><span>$0</span><span>$20,000</span></div>
                  </div>

                  {/* Bottleneck Select */}
                  <div className="space-y-2">
                    <Label>{t("whisperer.step2.bottleneck")} <span className="text-destructive">*</span></Label>
                    <Select value={bottleneck} onValueChange={setBottleneck}>
                      <SelectTrigger className={errors.bottleneck ? "border-destructive" : ""}>
                        <SelectValue placeholder={t("whisperer.step2.bottleneckPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {bottleneckOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.bottleneck && <p className="text-sm text-destructive">{errors.bottleneck}</p>}
                  </div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h3 className="font-display text-xl font-semibold text-foreground">{t("whisperer.step3.heading")}</h3>
                  <div className="space-y-2">
                    <Label htmlFor="audit_name">{t("whisperer.step3.name")} <span className="text-destructive">*</span></Label>
                    <Input id="audit_name" placeholder={t("whisperer.step3.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} className={errors.name ? "border-destructive" : ""} />
                    {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="audit_email">{t("whisperer.step3.email")} <span className="text-destructive">*</span></Label>
                    <Input id="audit_email" type="email" placeholder={t("whisperer.step3.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} className={errors.email ? "border-destructive" : ""} />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border/50">
              {currentStep > 1 ? (
                <Button variant="ghost" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("whisperer.back")}
                </Button>
              ) : <div />}

              {currentStep < 3 ? (
                <Button onClick={handleNext} style={{ background: "#2a7ffc" }} className="text-white hover:opacity-90">
                  {t("whisperer.next")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={isSubmitting} style={{ background: "#12cb89" }} className="text-white hover:opacity-90">
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("whisperer.submitting")}</>
                  ) : (
                    <>{t("whisperer.cta")}<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemsWhispererSection;
